import React from "react";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Switch } from "../ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from "../ui/alert-dialog";
import { LOCATIONS, ROLES } from "../../constants";

const selDark = "bg-vault-panel2 border-white/10 text-vault-cream h-11";

export const AwardGemsDialog = ({ open, onOpenChange, target, amt, setAmt, reason, setReason, onSubmit }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="bg-vault-panel border-white/10">
      <DialogHeader><DialogTitle className="text-vault-cream font-display">Award Gems {target ? `to ${target.firstName}` : ""}</DialogTitle></DialogHeader>
      <div className="space-y-4">
        <div className="space-y-2">
          <Label className="text-vault-subtle">Amount</Label>
          <Input type="number" value={amt} onChange={(e) => setAmt(e.target.value)} className="bg-vault-panel2 border-white/10 text-vault-cream" placeholder="50" data-testid="award-amount-input" />
        </div>
        <div className="space-y-2">
          <Label className="text-vault-subtle">Reason</Label>
          <Textarea value={reason} onChange={(e) => setReason(e.target.value)} className="bg-vault-panel2 border-white/10 text-vault-cream" placeholder="e.g. Outstanding contribution" data-testid="award-reason-input" />
        </div>
      </div>
      <DialogFooter>
        <button onClick={onSubmit} className="rounded-2xl bg-vault-accent text-vault-panel font-bold px-6 py-2.5 hover:bg-vault-accentdark transition-colors" data-testid="award-submit-button">Award Gems</button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);

export const AddUserDialog = ({ open, onOpenChange, newU, setNewField, addErr, addBusy, onSubmit }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="bg-vault-panel border-white/10 max-h-[90vh] overflow-y-auto">
      <DialogHeader><DialogTitle className="text-vault-cream font-display">Add User</DialogTitle></DialogHeader>
      <div className="grid sm:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label className="text-vault-subtle">First Name</Label>
          <Input value={newU.firstName} onChange={(e) => setNewField("firstName", e.target.value)} className={selDark} placeholder="Emma" data-testid="add-firstname-input" />
        </div>
        <div className="space-y-2">
          <Label className="text-vault-subtle">Last Name</Label>
          <Input value={newU.lastName} onChange={(e) => setNewField("lastName", e.target.value)} className={selDark} placeholder="Stone" data-testid="add-lastname-input" />
        </div>
        <div className="space-y-2 sm:col-span-2">
          <Label className="text-vault-subtle">Email</Label>
          <Input type="email" value={newU.email} onChange={(e) => setNewField("email", e.target.value)} className={selDark} placeholder="name@clarkacademy.com" data-testid="add-email-input" />
        </div>
        <div className="space-y-2 sm:col-span-2">
          <Label className="text-vault-subtle">Temporary Password</Label>
          <Input type="text" value={newU.password} onChange={(e) => setNewField("password", e.target.value)} className={selDark} placeholder="At least 6 characters" data-testid="add-password-input" />
        </div>
        <div className="space-y-2">
          <Label className="text-vault-subtle">Role</Label>
          <Select value={newU.role} onValueChange={(v) => setNewField("role", v)}>
            <SelectTrigger className={selDark}><SelectValue placeholder="Role" /></SelectTrigger>
            <SelectContent>{ROLES.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label className="text-vault-subtle">Primary Location</Label>
          <Select value={newU.primaryLocation} onValueChange={(v) => setNewField("primaryLocation", v)}>
            <SelectTrigger className={selDark}><SelectValue placeholder="Location" /></SelectTrigger>
            <SelectContent>{LOCATIONS.map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label className="text-vault-subtle">Birthdate</Label>
          <Input type="date" value={newU.birthdate} onChange={(e) => setNewField("birthdate", e.target.value)} className={`${selDark} [color-scheme:dark]`} />
        </div>
        <div className="space-y-2">
          <Label className="text-vault-subtle">Start Date</Label>
          <Input type="date" value={newU.startDate} onChange={(e) => setNewField("startDate", e.target.value)} className={`${selDark} [color-scheme:dark]`} />
        </div>
      </div>
      {addErr && <div className="text-sm text-red-400 bg-red-950/40 rounded-lg px-3 py-2" data-testid="add-user-error">{addErr}</div>}
      <DialogFooter>
        <button disabled={addBusy} onClick={onSubmit} className="rounded-2xl bg-vault-accent text-vault-panel font-bold px-6 py-2.5 hover:bg-vault-accentdark transition-colors disabled:opacity-60" data-testid="add-user-submit-button">
          {addBusy ? "Adding…" : "Create User"}
        </button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);

export const EditUserDialog = ({ open, onOpenChange, editTarget, editRole, setEditRole, editLoc, setEditLoc, editActive, setEditActive, editBusy, currentUserId, onSubmit }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="bg-vault-panel border-white/10">
      <DialogHeader><DialogTitle className="text-vault-cream font-display">Edit {editTarget ? `${editTarget.firstName} ${editTarget.lastName}` : "User"}</DialogTitle></DialogHeader>
      <div className="space-y-4">
        <div className="space-y-2">
          <Label className="text-vault-subtle">Role</Label>
          <Select value={editRole} onValueChange={setEditRole}>
            <SelectTrigger className={selDark} data-testid="edit-role-select"><SelectValue placeholder="Role" /></SelectTrigger>
            <SelectContent>{ROLES.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label className="text-vault-subtle">Primary Location</Label>
          <Select value={editLoc} onValueChange={setEditLoc}>
            <SelectTrigger className={selDark} data-testid="edit-location-select"><SelectValue placeholder="Location" /></SelectTrigger>
            <SelectContent>{LOCATIONS.map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="flex items-center justify-between rounded-xl bg-vault-panel2 px-4 py-3">
          <div>
            <div className="text-sm font-semibold text-vault-cream">Account Active</div>
            <div className="text-xs text-vault-subtle">Inactive users cannot sign in</div>
          </div>
          <Switch checked={editActive} onCheckedChange={setEditActive} disabled={editTarget && editTarget.id === currentUserId} data-testid="edit-active-switch" />
        </div>
      </div>
      <DialogFooter>
        <button disabled={editBusy} onClick={onSubmit} className="rounded-2xl bg-vault-accent text-vault-panel font-bold px-6 py-2.5 hover:bg-vault-accentdark transition-colors disabled:opacity-60" data-testid="edit-user-submit-button">
          {editBusy ? "Saving…" : "Save Changes"}
        </button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);

export const DeleteUserDialog = ({ delTarget, onOpenChange, onConfirm }) => (
  <AlertDialog open={!!delTarget} onOpenChange={onOpenChange}>
    <AlertDialogContent className="bg-vault-panel border-white/10">
      <AlertDialogHeader>
        <AlertDialogTitle className="text-vault-cream font-display">Delete user?</AlertDialogTitle>
        <AlertDialogDescription className="text-vault-subtle">
          This will permanently remove {delTarget ? `${delTarget.firstName} ${delTarget.lastName} (${delTarget.email})` : "this user"} and revoke their access. Their past requests are kept for records. This cannot be undone.
        </AlertDialogDescription>
      </AlertDialogHeader>
      <AlertDialogFooter>
        <AlertDialogCancel className="bg-vault-panel3 text-vault-cream border-0 hover:bg-vault-panel2">Cancel</AlertDialogCancel>
        <AlertDialogAction onClick={onConfirm} className="bg-red-700 text-white hover:bg-red-600" data-testid="confirm-delete-button">Delete</AlertDialogAction>
      </AlertDialogFooter>
    </AlertDialogContent>
  </AlertDialog>
);
